import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Badge, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, TextInput, Title,
} from '@tremor/react';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import Pagination from '../components/Pagination';
import TableSkeleton from '../components/TableSkeleton';
import { fetchInventorySkus } from '../api/inventory';
import { useStoreFilter } from '../lib/storeFilter';
import { usePagination } from '../lib/usePagination';
import { t } from '../lib/i18n';
import type { ABCClass, InventorySkuRow, User } from '../api/types';
import DrillCell from '../components/DrillCell';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 });
const fmtPct = new Intl.NumberFormat('es-MX', { style: 'percent', minimumFractionDigits: 1 });
const fmtPctCoarse = new Intl.NumberFormat('es-MX', { style: 'percent', maximumFractionDigits: 0 });

function abcBadge(c: ABCClass | undefined) {
  if (!c || c === '—') return <Badge color="gray">—</Badge>;
  const color = c === 'A' ? 'emerald' : c === 'B' ? 'amber' : 'red';
  return <Badge color={color}>{c}</Badge>;
}

interface Row extends InventorySkuRow {
  rank: number;
  share: number;
  cum_share: number;
}

export default function InventoryVelocity() {
  const user = useOutletContext<User>();
  const [skus, setSkus] = useState<InventorySkuRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Shared header store (lib/storeFilter); InventoryLayout renders the picker.
  const [sid] = useStoreFilter(user);
  const [klass, setKlass] = useState<'all' | ABCClass>('all');
  const [search, setSearch] = useState('');

  const load = () => {
    setErr(null);
    setSkus(null);
    fetchInventorySkus()
      .then(setSkus)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [user.role]);

  const rows = useMemo<Row[]>(() => {
    if (!skus) return [];
    let pool = skus.filter((r) => (r.margin90_mxn ?? 0) > 0);
    if (sid !== null) pool = pool.filter((r) => r.sid === sid);
    if (search) {
      const q = search.toUpperCase();
      pool = pool.filter((r) => r.part.toUpperCase().includes(q) || r.line.toUpperCase().includes(q));
    }
    // Rank by margin90 desc, then compute cumulative share per row.
    const sorted = [...pool].sort((a, b) => (b.margin90_mxn ?? 0) - (a.margin90_mxn ?? 0));
    const total = sorted.reduce((acc, r) => acc + (r.margin90_mxn ?? 0), 0);
    let cum = 0;
    const out: Row[] = [];
    for (let i = 0; i < sorted.length; i++) {
      const r = sorted[i];
      const m = r.margin90_mxn ?? 0;
      const share = total > 0 ? m / total : 0;
      cum += share;
      out.push({ ...r, rank: i + 1, share, cum_share: cum });
    }
    return klass === 'all' ? out : out.filter((r) => r.abc_class === klass);
  }, [skus, sid, search, klass]);

  const pg = usePagination(rows);
  const showCost = user.role !== 'staff';

  if (!showCost) {
    return <Text>{t.inventory.gmroiNoCost}</Text>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Title>{t.inventory.velocityTitle}</Title>
          <Text className="text-tremor-content text-sm">{t.inventory.velocitySubtitle}</Text>
        </div>
      </div>

      <Card className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <div className="text-tremor-content text-xs mb-1">{t.inventory.filter.abc}</div>
          <div className="flex gap-1">
            {(['all', 'A', 'B', 'C', '—'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setKlass(v as typeof klass)}
                className={
                  'px-2 py-1 rounded border text-xs ' +
                  (klass === v
                    ? 'bg-tremor-brand text-tremor-brand-inverted border-tremor-brand'
                    : 'border-tremor-border text-tremor-content hover:text-tremor-content-strong')
                }
              >
                {v === 'all' ? t.inventory.filter.abcAll : v}
              </button>
            ))}
          </div>
        </label>
        <label className="text-sm">
          <div className="text-tremor-content text-xs mb-1">{t.inventory.velocityCols.part}</div>
          <TextInput value={search} onValueChange={setSearch} placeholder="X1234" className="w-40" />
        </label>
        <div className="ml-auto text-sm text-tremor-content">
          {rows.length} SKU(s)
        </div>
      </Card>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && skus === null && <TableSkeleton rows={8} cols={9} />}
      {!err && skus !== null && rows.length === 0 && <EmptyState message="—" />}
      {!err && rows.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell className="text-right">#</TableHeaderCell>
                <TableHeaderCell>{t.inventory.velocityCols.part}</TableHeaderCell>
                <TableHeaderCell>{t.inventory.velocityCols.pdesc}</TableHeaderCell>
                <TableHeaderCell className="text-right">Tienda</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.inventory.velocityCols.onHand}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.inventory.velocityCols.units}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.inventory.velocityCols.margin}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.inventory.velocityCols.share}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.inventory.velocityCols.cum}</TableHeaderCell>
                <TableHeaderCell>{t.inventory.velocityCols.abc}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pg.pageRows.map((r) => (
                <TableRow key={`${r.sid}-${r.line}-${r.part}`}>
                  <TableCell className="text-right">{r.rank}</TableCell>
                  <TableCell className="font-medium">
                    <DrillCell target={{ kind: 'inv', sid: r.sid, line: r.line, part: r.part }}>
                      {r.line} {r.part}
                    </DrillCell>
                  </TableCell>
                  <TableCell>{r.pdesc}</TableCell>
                  <TableCell className="text-right">{r.sid}</TableCell>
                  <TableCell className="text-right">{r.on_hand}</TableCell>
                  <TableCell className="text-right">{r.units90}</TableCell>
                  <TableCell className="text-right">
                    {r.margin90_mxn == null ? '—' : fmtMxn.format(r.margin90_mxn)}
                  </TableCell>
                  <TableCell className="text-right">{fmtPct.format(r.share)}</TableCell>
                  <TableCell className="text-right">{fmtPctCoarse.format(r.cum_share)}</TableCell>
                  <TableCell>{abcBadge(r.abc_class)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination {...pg} className="px-4" />
        </Card>
      )}
    </div>
  );
}
